import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  REPORT_EXPORT_FORMATS,
  isAppKey,
  type AppKey,
  type ReportExportFormat,
  type ReportParamKind,
  type ReportResult,
} from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../components/AppTopBar.js';
import { PageMessage } from './AppHome.js';
import { ApiError, fetchPeriods } from '../lib/api.js';
import { fetchReference } from '../lib/lesson-api.js';
import { downloadReport, fetchReport, fetchReportList, type ReportFilters, type ReportSummary } from '../lib/reports-api.js';
import { localName, t, formatDateTime } from '../lib/i18n.js';

/**
 * The Reports Hub (spec §13.8), per application.
 *
 * The chosen report and its filters live in the address (`?report=…&from=…`),
 * so a report can be bookmarked or sent to a colleague — who sees it through
 * their OWN scope, not the sender's.
 */

const FORMAT_LABELS: Record<ReportExportFormat, string> = { csv: 'CSV', xlsx: 'Excel', pdf: 'PDF' };
const FILTER_KEYS: ReportParamKind[] = ['from', 'to', 'projectId', 'year', 'periodId'];

export function Reports() {
  const params = useParams<{ app: string }>();
  const [search, setSearch] = useSearchParams();
  const app = params.app;
  const list = useQuery({
    queryKey: ['reports', app],
    queryFn: () => fetchReportList(app!),
    enabled: Boolean(app && (isAppKey(app) || app === 'executive')),
  });

  if (!app || (!isAppKey(app) && app !== 'executive')) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  const selectedKey = search.get('report');
  const selected = list.data?.reports.find((report) => report.key === selectedKey) ?? null;

  const choose = (key: string | null) => {
    const next = new URLSearchParams();
    if (key) next.set('report', key);
    setSearch(next);
  };

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey | 'executive'} />
      <Breadcrumbs app={app as AppKey | 'executive'} trail={selected ? ['Reports', selected.label] : ['Reports']} />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-4">
        {!selected ? (
          <>
            <h1 className="m-0 text-2xl font-semibold">{t("Reports")}</h1>
            <p className="mt-1 text-sm text-content-secondary">
              {t("Each report shows only the records your role lets you see. Opening or exporting one is recorded.")}
            </p>
            {list.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
            {list.data && list.data.reports.length === 0 && (
              <p className="mt-6 rounded-md border border-border bg-tone-info-surface p-5 text-sm" role="status">
                {t("No reports are available to your role in this application.")}
              </p>
            )}
            <ul className="m-0 mt-6 grid list-none gap-3 p-0 sm:grid-cols-2">
              {list.data?.reports.map((report) => (
                <li key={report.key} className="rounded-md border border-border p-4">
                  <button type="button" onClick={() => choose(report.key)} className="text-start text-ag-purple underline">
                    {t(report.label)}
                  </button>
                  <p className="mb-0 mt-1 text-sm text-content-secondary">{t(report.description)}</p>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <ReportView
            app={app as AppKey | 'executive'}
            summary={selected}
            canExport={list.data?.canExport ?? false}
            filters={Object.fromEntries(FILTER_KEYS.flatMap((key) => (search.get(key) ? [[key, search.get(key)!]] : [])))}
            onFilters={(filters) => {
              const next = new URLSearchParams({ report: selected.key });
              for (const [key, value] of Object.entries(filters)) if (value) next.set(key, value);
              setSearch(next);
            }}
            onBack={() => choose(null)}
          />
        )}
      </main>
    </div>
  );
}

function ReportView({
  app,
  summary,
  canExport,
  filters,
  onFilters,
  onBack,
}: {
  app: AppKey | 'executive';
  summary: ReportSummary;
  canExport: boolean;
  filters: ReportFilters;
  onFilters: (filters: ReportFilters) => void;
  onBack: () => void;
}) {
  const [draft, setDraft] = useState<ReportFilters>(filters);
  const [exporting, setExporting] = useState<ReportExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const missing = summary.required.filter((key) => !filters[key]);

  const report = useQuery({
    queryKey: ['report', summary.key, filters],
    queryFn: () => fetchReport(summary.key, filters),
    enabled: missing.length === 0,
    retry: false,
    staleTime: 0,
  });
  const reference = useQuery({
    queryKey: ['reference', app],
    queryFn: () => fetchReference(app),
    // The project list also names the monthly reports in the period picker.
    enabled: app !== 'executive' && (summary.params.includes('projectId') || summary.params.includes('periodId')),
  });
  const periods = useQuery({ queryKey: ['periods'], queryFn: fetchPeriods, enabled: summary.params.includes('periodId') });

  const onExport = async (format: ReportExportFormat) => {
    setExporting(format);
    setExportError(null);
    try {
      await downloadReport(summary.key, filters, format);
    } catch (caught) {
      setExportError(caught instanceof ApiError ? caught.message : 'The export failed.');
    } finally {
      setExporting(null);
    }
  };

  const projectCode = new Map((reference.data?.projects ?? []).map((project) => [project.id, project.code]));

  return (
    <>
      <button type="button" onClick={onBack} className="text-sm text-ag-purple underline">
        {t("All reports")}
      </button>
      <h1 className="m-0 mt-2 text-2xl font-semibold">{t(summary.label)}</h1>
      <p className="mt-1 text-sm text-content-secondary">{t(summary.description)}</p>

      <form
        className="mt-4 flex flex-wrap items-end gap-3 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          onFilters(draft);
        }}
      >
        {summary.params.includes('from') && (
          <label className="flex flex-col gap-1">
            <span className="text-content-secondary">{t("From")}</span>
            <input type="date" value={draft.from ?? ''} onChange={(event) => setDraft({ ...draft, from: event.target.value })} className="rounded border border-border px-2 py-1" />
          </label>
        )}
        {summary.params.includes('to') && (
          <label className="flex flex-col gap-1">
            <span className="text-content-secondary">{t("To")}</span>
            <input type="date" value={draft.to ?? ''} onChange={(event) => setDraft({ ...draft, to: event.target.value })} className="rounded border border-border px-2 py-1" />
          </label>
        )}
        {summary.params.includes('projectId') && (
          <label className="flex flex-col gap-1">
            <span className="text-content-secondary">{t("Project")}</span>
            <select value={draft.projectId ?? ''} onChange={(event) => setDraft({ ...draft, projectId: event.target.value })} className="rounded border border-border px-2 py-1">
              <option value="">{t("All my projects")}</option>
              {(reference.data?.projects ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code} — {localName(project)}
                </option>
              ))}
            </select>
          </label>
        )}
        {summary.params.includes('year') && (
          <label className="flex flex-col gap-1">
            <span className="text-content-secondary">{t("Year")}</span>
            <input type="number" min={2000} max={2100} value={draft.year ?? ''} placeholder={String(new Date().getFullYear())} onChange={(event) => setDraft({ ...draft, year: event.target.value })} className="w-24 rounded border border-border px-2 py-1" />
          </label>
        )}
        {summary.params.includes('periodId') && (
          <label className="flex flex-col gap-1">
            <span className="text-content-secondary">{t("Monthly report")}</span>
            <select value={draft.periodId ?? ''} onChange={(event) => setDraft({ ...draft, periodId: event.target.value })} className="rounded border border-border px-2 py-1">
              <option value="">{t("Choose…")}</option>
              {(periods.data?.periods ?? []).map((period) => (
                <option key={period.id} value={period.id}>
                  {projectCode.get(period.projectId) ?? t("Project")} — {period.periodStart.slice(0, 7)} ({period.status.replace('_', ' ')})
                </option>
              ))}
            </select>
          </label>
        )}
        <button type="submit" className="rounded bg-ag-purple px-3 py-1.5 text-white">
          {t("Show")}
        </button>
        {canExport && missing.length === 0 && (
          <div className="ms-auto flex gap-2">
            {REPORT_EXPORT_FORMATS.map((format) => (
              <button
                key={format}
                type="button"
                disabled={exporting !== null}
                onClick={() => void onExport(format)}
                className="rounded border border-border px-3 py-1.5 disabled:opacity-60"
              >
                {exporting === format ? t("Exporting…") : t("Export {0}", { 0: FORMAT_LABELS[format] })}
              </button>
            ))}
          </div>
        )}
      </form>

      {exportError && (
        <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {exportError}
        </p>
      )}
      {missing.length > 0 && (
        <p className="mt-6 text-sm text-content-secondary" role="status">
          {t("Choose the filters above to run this report.")}
        </p>
      )}
      {report.isLoading && missing.length === 0 && <p className="mt-6 text-content-secondary">{t("Running the report…")}</p>}
      {report.error && (
        <p role="alert" className="mt-6 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {report.error instanceof ApiError ? report.error.message : t("The report could not be run.")}
        </p>
      )}
      {report.data && <ReportTables result={report.data.report} />}
    </>
  );
}

function ReportTables({ result }: { result: ReportResult }) {
  return (
    <div className="mt-6" data-testid="report-result">
      {result.subtitle && <p className="m-0 text-sm text-content-secondary">{result.subtitle}</p>}
      {result.sections.map((section) => (
        <section key={t(section.heading)} className="mt-6">
          <h2 className="m-0 text-lg font-semibold">{t(section.heading)}</h2>
          {section.rows.length === 0 ? (
            <p className="mt-2 text-sm text-content-secondary">{t(section.emptyText || 'Nothing to show.')}</p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-content-secondary">
                    {section.columns.map((column) => (
                      <th key={column.key} className={`border-b border-border p-2 font-normal ${column.numeric ? 'text-end' : 'text-start'}`}>
                        {t(column.label)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row, index) => (
                    <tr key={index}>
                      {section.columns.map((column) => (
                        <td key={column.key} className={`border-b border-border p-2 align-top ${column.numeric ? 'text-end' : ''}`}>
                          {row[column.key] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
      {result.caveats.length > 0 && (
        <section className="mt-8 rounded-md border border-border bg-surface p-4 text-sm" data-testid="report-caveats">
          <h2 className="m-0 text-base font-semibold">{t("What this report cannot show")}</h2>
          <ul className="mb-0 mt-2">
            {result.caveats.map((caveat) => (
              <li key={caveat}>{t(caveat)}</li>
            ))}
          </ul>
        </section>
      )}
      <p className="mt-6 text-xs text-content-secondary">
        {t("Generated")}{' '}{formatDateTime(result.generatedAt)}.
      </p>
    </div>
  );
}
